import {
  Box,
  Paper,
  Skeleton,
  Stack,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SwayyCategoriesStore } from "../../../stores/SwayyCategoriesStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";
import { SwayyPageHeader } from "../../components/SwayyPageShell";

const primaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
  alignSelf: "flex-start",
} as const;

const secondaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
} as const;

const SwayyCategoryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const categoryId = Number(id);
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const storeRef = useRef(new SwayyCategoriesStore());
  const store = storeRef.current;
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  useEffect(() => {
    if (!canViewCatalog) return;
    if (!Number.isInteger(categoryId) || categoryId <= 0) return;
    void store.loadCategory(categoryId).then((error) => {
      if (error) {
        showErrorRef.current("Error", "Failed to load category.");
      }
    });
    return () => store.dispose();
  }, [canViewCatalog, categoryId, store]);

  const detail = store.categoryDetail;
  const optionList: FilterValue[] = store.catalogOptions.map((option) => ({
    id: String(option.id),
    value: `${option.name} · ${option.values.length} values`,
  }));
  const selectedOptions: FilterValue[] = optionList.filter((item) =>
    store.draftOptionIds.includes(Number(item.id)),
  );

  const handleSaveOptions = async () => {
    const error = await store.saveCategoryOptions(categoryId);
    if (error) {
      showError("Error", "Failed to save options.");
      return;
    }
    showSuccess("Category options updated.");
  };

  if (!canViewCatalog) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <SwayyPageHeader
        title={detail?.name || "Category"}
        description="Link catalog options so products in this category see them under Available options."
        actions={
          <AppButton
            label="Back to categories"
            variant="secondary"
            onClick={() => navigate("/categories")}
            customStyles={secondaryBtnSx}
          />
        }
      />

      {store.detailState === "loading" || store.detailState === "idle" ? (
        <Skeleton variant="rounded" height={220} />
      ) : !detail ? (
        <CustomTypography variant="body1" color={swayyColors.danger}>
          Category not found.
        </CustomTypography>
      ) : (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: swayyRadii.lg,
            borderColor: swayyColors.border,
          }}
        >
          <CustomTypography
            variant="body2"
            color={swayyColors.textSecondary}
            sx={{ mb: 2 }}
          >
            SKU {detail.skuCode || "—"} ·{" "}
            {detail.isActive ? "Active" : "Inactive"}
          </CustomTypography>

          <CustomTypography
            variant="heading5"
            color={swayyColors.deepGreen}
            sx={{ fontWeight: 700, mb: 1 }}
          >
            Category options
          </CustomTypography>
          <CustomTypography
            variant="caption"
            color={swayyColors.textSecondary}
            sx={{ display: "block", mb: 1.5 }}
          >
            Select options to make them eligible for products in this category.
          </CustomTypography>

          <Stack spacing={2} maxWidth={520}>
            <AppSearchDropdownMulti
              isMultiSelect
              title="Select options"
              list={optionList}
              value={selectedOptions}
              local
              disabled={!canEditCatalog}
              accentColor={swayyColors.pine}
              borderColor={swayyColors.border}
              borderRadius={swayyRadii.md}
              handleSelect={(selected) => {
                if (!canEditCatalog) return;
                store.setDraftOptionIds(
                  selected
                    .map((item) => Number(item.id))
                    .filter((optionId) => Number.isInteger(optionId) && optionId > 0),
                );
              }}
            />
            {canEditCatalog && (
              <AppButton
                label={store.isSaving ? "Saving..." : "Save options"}
                variant="primary"
                disabled={!store.canSaveOptions || store.isSaving}
                onClick={() => void handleSaveOptions()}
                customStyles={primaryBtnSx}
              />
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default observer(SwayyCategoryDetail);